





































import { Component, Vue, Prop } from 'vue-property-decorator'
import BaseImage from 'vuecomponents/base_image.vue'
import { proxiedUrl, processedUrl } from '@padlet/vivaldi-client'
import device from 'bits/device'
import overflowBorderRadiusTransformFixStyle from 'bits/overflow_border_radius_transform_fix'

/**
 * Thumbnail that renders a colored placeholder while image is loading.
 * @displayName Image Thumbnail
 */
@Component({
  components: { BaseImage },
})
export default class ImageThumbnail extends Vue {
  loaded = false
  error = false
  isSafari = device?.safari

  /**
   * URL for image
   */
  @Prop({ required: true }) src!: string

  /**
   * Original image width
   */
  @Prop({ required: true }) originalImageWidth!: number

  /**
   * Original image height
   */
  @Prop({ required: true }) originalImageHeight!: number

  /**
   * Resulting component width
   */
  @Prop({ required: true }) width!: number

  /**
   * Resize strategy. Ignored if no aspect ratio is provided.
   * @values crop, stretch
   */
  @Prop({ default: 'crop' }) resizeStrategy?: 'crop' | 'stretch'

  /**
   * If specified, thumbnail height will be computed based on this value.
   * Value should be `width / height`, i.e. `2` will be wide and `0.5` will be tall.
   */
  @Prop() aspectRatio?: number

  /**
   * Title to display when the image fails to load
   */
  @Prop({ default: '' }) title!: string

  /**
   * Determines if we should load the image lazily.
   */
  @Prop({ default: true }) lazyLoading!: boolean

  /**
   * Determines whether image should be proxied to vivaldi for caching
   */
  @Prop({ default: false }) proxyImage!: boolean

  /**
   * Determines whether image should be processed before loading
   */
  @Prop({ default: false }) processImage!: boolean

  /**
   * Placeholder color for the thumbnail.
   * @values red, blue, green, yellow, purple, pink, cyan, orange, transparent
   */
  @Prop({ default: 'transparent' }) placeholderColor!: string

  get overflowBorderRadiusTransformFixStyle(): Record<string, string> {
    return overflowBorderRadiusTransformFixStyle(this.isSafari)
  }

  get bgColorClass(): string {
    if (this.loaded) return ''

    switch (this.placeholderColor) {
      case 'red':
        return 'bg-bgi-red'
      case 'blue':
        return 'bg-bgi-blue'
      case 'green':
        return 'bg-bgi-green'
      case 'yellow':
        return 'bg-bgi-yellow'
      case 'purple':
        return 'bg-bgi-purple'
      case 'pink':
        return 'bg-bgi-pink'
      case 'cyan':
        return 'bg-bgi-cyan'
      case 'orange':
        return 'bg-bgi-orange'
      case 'grey':
        return 'bg-highway-200'
      case 'transparent':
      default:
        return ''
    }
  }
  get url(): string {
    if (this.processImage) {
      const processingOptions: { width: number; ar?: number } = { width: this.width }
      if (this.resizeStrategy === 'stretch' && this.aspectRatio) {
        processingOptions.ar = this.aspectRatio
      }
      // Vivaldi can also crop the image for us.
      // We don't request cropped images because we want to avoid spamming Vivaldi as the image is resized.
      // We can revisit this.
      return processedUrl(this.src, processingOptions)
    }
    return this.proxyImage ? proxiedUrl(this.src) : this.src
  }
  get originalImageAspectRatio(): number {
    return this.originalImageWidth / this.originalImageHeight
  }

  get dimensions(): {
    cropFrameWidth: number
    cropFrameHeight: number
    cropFrameAspectRatio: number
    imageElementWidth: number
    imageElementHeight: number
  } {
    // Width is fixed.
    const cropFrameWidth = this.width

    // These are decided based on the resizing strategy.
    let cropFrameHeight
    let cropFrameAspectRatio
    let imageElementHeight
    let imageElementWidth

    // CASE 1: Crop to fit aspect ratio
    if (this.resizeStrategy === 'crop' && this.aspectRatio) {
      // Try to fit as much of the image in the crop frame as possible.
      cropFrameAspectRatio = this.aspectRatio
      cropFrameHeight = cropFrameWidth / cropFrameAspectRatio
      const originalHasWiderAspectRatio = this.originalImageAspectRatio > this.aspectRatio
      if (originalHasWiderAspectRatio) {
        imageElementHeight = cropFrameHeight
        imageElementWidth = this.originalImageAspectRatio * cropFrameHeight
      } else {
        imageElementWidth = cropFrameWidth
        imageElementHeight = imageElementWidth / this.originalImageAspectRatio
      }

      // CASE 2: Stretch to fit aspect ratio
    } else if (this.resizeStrategy === 'stretch' && this.aspectRatio) {
      // Image element and crop frame both have the same dimensions.
      cropFrameAspectRatio = this.aspectRatio
      cropFrameHeight = cropFrameWidth / cropFrameAspectRatio
      imageElementWidth = cropFrameWidth
      imageElementHeight = cropFrameHeight

      // CASE 3: Scale, keeping original aspect ratio
    } else {
      // Maintain the same ratio as the original, but scale down proportionately if need be.
      cropFrameAspectRatio = this.originalImageAspectRatio
      cropFrameHeight = cropFrameWidth / cropFrameAspectRatio
      imageElementWidth = cropFrameWidth
      imageElementHeight = cropFrameHeight
    }

    return { cropFrameWidth, cropFrameHeight, cropFrameAspectRatio, imageElementWidth, imageElementHeight }
  }

  onLoad(): void {
    this.loaded = true
    this.$emit('load')
  }
  onError(): void {
    this.error = true
    this.$emit('error')
  }
}
